import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Image,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
} from "@chakra-ui/react";
import { HamburgerIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { logoutToLanding } from "../../lib/logout";
import NotificationPopover from "../../components/NotificationPopover";
import { getPlatform } from "../../lib/platformUtils";
import { tt } from "../../i18n/tt";
import { lazyI18n } from "../../i18n/lazyText";
import { useTranslation } from "react-i18next";
import { Globe } from "react-feather";
import { LanguagePill, LanguageSheet, useLanguagePreference } from "../../components/LanguageSwitcher";
import LanguageFlag from "../../components/LanguageFlag";
import { useRightsTodo } from "../../contexts/RightsTodoContext";
import { todoBadgeText, RIGHTS_TODO_PATH } from "../../lib/rightsTodo";
import ImpersonateDrawer, { ImpersonateDrawerButton, ImpersonateViewingBar, revertImpersonation } from "../../components/admin/ImpersonateDrawer";

// iOS 만 status bar 만큼 nav 상단 패딩 강제. Android/Web 은 이전 동작 유지.
const TOP_INSET_STYLE = getPlatform() === "ios"
  ? { paddingTop: "max(env(safe-area-inset-top), 47px)" }
  : undefined;

const NAV_ITEMS = lazyI18n(() => [
  { label: tt("a.9190db22", "작품"), path: "/video/rights-manager" },
  // 2026-10-21 「검수」 → 「검수/화리」 — 검수 답하기와 화이트리스트 처리를 한 탭에서(대표 결정 · 시안 docs/design/rights-todo-inbox/).
  //   todo: 할 일 숫자 배지를 붙이고, 할 일이 있으면 「할 일 전체」(RIGHTS_TODO_PATH)로 연다.
  { label: tt("a.todo.nav", "검수/화리"), path: "/video/inspection-manage", todo: true },
  { label: tt("a.94f0d012", "수익"), path: "/video-dashboard" },
  { label: tt("a.50f3392c", "인사이트"), path: "/video-insight" },
  { label: tt("a.15d47b30", "정산"), path: "/total-copyrights" },
  // 레이블리 쇼츠(/shorts)는 2026-09-22 대표 결정(시안 C)으로 가운데 메뉴에서 빼고 오른쪽 도구 영역의 채운 버튼(ShortsButton)으로 옮겼다.
  // 시안 docs/design/rights-topbar-shorts-menu-variants.html · 휴대폰 ≡ 메뉴에도 넣지 않는다(상단바에 상시 노출).
]);

// 「▶ 쇼츠」 버튼 — 다른 서비스(/shorts 독립 트리)로 가는 문. 🔒 반드시 <a href> 전체 페이지 이동(인수인계 함정 1, 라우터 navigate 금지).
// PC 는 언어 알약과 같은 30px 높이 + BETA 배지(/shorts 상단바의 BETA 와 맞춤), 휴대폰은 28px · 배지 없음.
function ShortsButton() {
  return (
    <Box
      as="a"
      href="/shorts"
      data-track="권리사 상단바 쇼츠"
      display="inline-flex"
      alignItems="center"
      gap={1.5}
      h={{ base: "28px", md: "30px" }}
      pl={{ base: 2, md: 2.5 }}
      pr={{ base: 2.5, md: 3 }}
      mr={1}
      borderRadius="8px"
      bg="brand.500"
      color="white"
      fontWeight="700"
      fontSize={{ base: "12.5px", md: "13px" }}
      lineHeight="1"
      whiteSpace="nowrap"
      boxShadow="0 1px 2px rgba(60,183,160,0.3)"
      transition="background 0.15s ease"
      _hover={{ bg: "brand.600", textDecoration: "none" }}
      _active={{ bg: "brand.700" }}
    >
      <Box as="span" w={0} h={0} borderLeft="7px solid white" borderTop="4.5px solid transparent" borderBottom="4.5px solid transparent" aria-hidden />
      <Text as="span">{tt("a.a50a6396", "쇼츠")}</Text>
      <Text as="span" display={{ base: "none", md: "inline" }} fontSize="9.5px" fontWeight="800" letterSpacing="0.04em" bg="whiteAlpha.300" borderRadius="4px" px="4px" py="1px" ml="2px">BETA</Text>
    </Box>
  );
}

// 할 일 숫자 — 검수 답할 건 + 화이트리스트 처리할 건(src/lib/rightsTodo.js). 0 이면 그리지 않는다.
function TodoCountBadge({ count }) {
  const text = todoBadgeText(count);
  if (!text) return null;
  return (
    <Box
      as="span"
      data-rights-todo-count={count}
      minW="20px"
      h="20px"
      px="6px"
      borderRadius="full"
      bg="status.error"
      color="white"
      fontSize="12px"
      fontWeight="800"
      lineHeight="20px"
      textAlign="center"
      fontVariantNumeric="tabular-nums"
    >
      {text}
    </Box>
  );
}

// 메뉴 이동 — 「검수/화리」는 할 일이 있으면 「할 일 전체」 보기로 연다
const navTarget = (item, todoTotal) => (item.todo && todoTotal > 0 ? RIGHTS_TODO_PATH : item.path);

export default function VideoRightsTopBar() {
  const { t } = useTranslation();
  const { lang: currentLang } = useLanguagePreference();
  const [langSheetOpen, setLangSheetOpen] = useState(false);
  const { userInfo, isLoading } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { todo } = useRightsTodo();
  const todoTotal = todo?.total || 0;

  // 운영 서랍 「회원 화면 보기」(2026-10-20 시안 C) — 홈 TopNavBar 와 같은 부품. 열기·「돌아가기」는 ImpersonateDrawer 에.
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [impersonateMode] = useState(!!localStorage.getItem("impersonateEmail"));
  const [isActualAdmin] = useState(localStorage.getItem("isActualAdmin") === "true");
  const handleRevert = () => revertImpersonation(navigate);

  useEffect(() => {
    if (isLoading) return;
    if (!isLoading && userInfo && !userInfo.role?.includes("Copyrights Holder")) {
      navigate("/home");
    }
  }, [userInfo, isLoading, navigate]);

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={1000}
      bg="white"
      borderBottom="1px solid"
      borderColor="neutral.200"
      style={TOP_INSET_STYLE}
    >
      <Flex
        h="56px"
        align="center"
        justify="space-between"
        px={{ base: 4, md: 6 }}
        maxW="100%"
        position="relative"
      >
        {/* Logo */}
        <Flex align="center" gap={2}>
          <Image
            src="/laeebly-icon.png"
            alt={tt("a.0d69c179", "로고")}
            boxSize="28px"
            cursor="pointer"
            onClick={() => navigate("/home")}
          />
          <Text
            fontWeight="700"
            fontSize="md"
            color="neutral.800"
            letterSpacing="-0.5px"
          >
            {tt("a.734f3bfb", "레이블리")}
          </Text>
        </Flex>

        {/* Desktop horizontal nav — absolute center */}
        <Flex
          display={{ base: "none", md: "flex" }}
          align="center"
          gap={1}
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
        >
          {NAV_ITEMS.map((item) => {
            const isActive = currentPath === item.path;
            return (
              <Flex
                key={item.path}
                // 2026-10-21 진짜 버튼으로 — 키보드로 닿고 aria-label(할 일 수)이 보조기기에 읽힌다
                as="button"
                type="button"
                bg="transparent"
                border="none"
                aria-current={isActive ? "page" : undefined}
                align="center"
                gap={1.5}
                px={3}
                py={1.5}
                borderRadius="6px"
                cursor="pointer"
                color={isActive ? "brand.600" : "neutral.500"}
                fontWeight={isActive ? "600" : "500"}
                fontSize="sm"
                position="relative"
                transition="all 0.15s ease"
                _hover={{ color: "brand.600", bg: "neutral.50" }}
                _after={isActive ? {
                  content: '""',
                  position: "absolute",
                  bottom: "-9px",
                  left: "0",
                  right: "0",
                  height: "2px",
                  bg: "brand.500",
                  borderRadius: "1px",
                } : {}}
                onClick={() => (item.external ? window.location.assign(item.path) : navigate(navTarget(item, todoTotal)))}
                aria-label={item.todo && todoTotal > 0 ? tt("a.todo.navAria", "{{v0}} — 처리할 일 {{v1}}건", { v0: item.label, v1: todoTotal }) : undefined}
              >
                <Text>{item.label}</Text>
                {item.todo && <TodoCountBadge count={todoTotal} />}
              </Flex>
            );
          })}
        </Flex>

        {/* Right side: notification + user menu */}
        <Flex align="center" gap={1}>
          {/* 2026-09-22 대표 결정(시안 C) — 「▶ 쇼츠」 채운 버튼을 언어 알약 왼쪽에. 휴대폰에서도 ≡ 를 열지 않고 보인다 */}
          <ShortsButton />
          {/* 피씨 A안 — 상단바 알약. 휴대폰에서는 숨고 아래 ≡ 메뉴 첫 줄로 간다(app-i18n-plan.md §4) */}
          <Box mr={1}><LanguagePill /></Box>
          <NotificationPopover />
          <Menu placement="bottom-end" autoSelect={false}>
            <MenuButton
              as={IconButton}
              aria-label={todoTotal > 0 ? tt("a.todo.menuAria", "메뉴 — 처리할 일 {{v0}}건", { v0: todoTotal }) : tt("a.5bc9d189", "메뉴")}
              icon={
                <Box as="span" position="relative" display="inline-flex">
                  <HamburgerIcon />
                  {/* 휴대폰은 상단 메뉴가 ≡ 안에 있어 숫자가 안 보인다 → 빨간 점(PC 는 가운데 메뉴에 숫자가 있어 숨김) */}
                  {todoTotal > 0 && (
                    <Box as="span" display={{ base: "block", md: "none" }} position="absolute" top="-3px" right="-4px" w="8px" h="8px" borderRadius="full" bg="status.error" aria-hidden />
                  )}
                </Box>
              }
              variant="ghost"
              size="sm"
              borderRadius="6px"
              color="neutral.500"
              _hover={{ bg: "neutral.50", color: "neutral.800" }}
            />
            <MenuList
              minW="200px"
              borderRadius="8px"
              border="1px solid"
              borderColor="neutral.200"
              boxShadow="0 4px 16px rgba(0,0,0,0.08)"
              p={1}
              bg="white"
            >
              {/* User info */}
              <Box px={3} py={2} mb={1}>
                <Text fontSize="sm" fontWeight="600" color="neutral.800">
                  {userInfo?.name}
                </Text>
                <Text fontSize="xs" color="neutral.400">
                  {userInfo?.email}
                </Text>
              </Box>
              <Divider borderColor="neutral.100" />

              {/* 휴대폰 B안 — ≡ 메뉴 첫 줄 「언어」 → 바텀시트 */}
              <MenuItem
                display={{ base: "flex", md: "none" }}
                fontSize="sm"
                fontWeight="500"
                py={2}
                px={3}
                mt={1}
                borderRadius="4px"
                color="neutral.600"
                _hover={{ bg: "neutral.50" }}
                onClick={() => setLangSheetOpen(true)}
              >
                <Flex align="center" gap={2} w="100%">
                  <Box as={Globe} size={16} color="neutral.500" aria-hidden />
                  <Text as="span">{t("lang.label")}</Text>
                  <Flex align="center" gap={1.5} ml="auto">
                    <LanguageFlag lang={currentLang} size={18} />
                    <Text as="span" fontSize="xs" color="neutral.400">{t(`lang.${currentLang}`)}</Text>
                    <ChevronRightIcon color="neutral.300" boxSize={4} />
                  </Flex>
                </Flex>
              </MenuItem>

              {/* Mobile-only nav items */}
              <Box display={{ base: "block", md: "none" }} py={1}>
                {NAV_ITEMS.map((item) => (
                  <MenuItem
                    key={item.path}
                    fontSize="sm"
                    fontWeight="500"
                    py={2}
                    px={3}
                    borderRadius="4px"
                    color={currentPath === item.path ? "brand.600" : "neutral.600"}
                    bg={currentPath === item.path ? "brand.50" : "transparent"}
                    _hover={{ bg: "neutral.50" }}
                    onClick={() => (item.external ? window.location.assign(item.path) : navigate(navTarget(item, todoTotal)))}
                  >
                    <Flex align="center" justify="space-between" w="100%" gap={2}>
                      <Text as="span">{item.label}</Text>
                      {item.todo && <TodoCountBadge count={todoTotal} />}
                    </Flex>
                  </MenuItem>
                ))}
                <Divider my={1} borderColor="neutral.100" />
              </Box>

              <Box py={1}>
                <MenuItem
                  fontSize="sm" fontWeight="500" py={2} px={3} borderRadius="4px"
                  _hover={{ bg: "neutral.50" }} color="neutral.600"
                  onClick={() => navigate("/video/settings")}
                >
                  {tt("a.109cc06d", "설정")}
                </MenuItem>
                <MenuItem
                  fontSize="sm" fontWeight="500" py={2} px={3} borderRadius="4px"
                  _hover={{ bg: "neutral.50" }} color="neutral.600"
                  onClick={() => navigate("/video/notice")}
                >
                  {tt("a.a6deb04d", "공지사항")}
                </MenuItem>
              </Box>
              <Divider borderColor="neutral.100" />
              <Box py={1}>
                <MenuItem
                  fontSize="sm"
                  fontWeight="500"
                  py={2}
                  px={3}
                  borderRadius="4px"
                  color="status.error"
                  _hover={{ bg: "red.50" }}
                  onClick={logoutToLanding}
                >
                  {tt("a.9ab19a87", "로그아웃")}
                </MenuItem>
              </Box>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>

      {/* Admin: 운영 서랍 「회원 화면 보기」 */}
      {!impersonateMode && userInfo?.role?.includes("Admin") && (
        <Flex
          px={{ base: 4, md: 6 }}
          py={2}
          borderTop="1px solid"
          borderColor="neutral.100"
          bg="neutral.50"
          align="center"
          justify="flex-end"
        >
          <ImpersonateDrawerButton onClick={() => setViewAsOpen(true)} />
        </Flex>
      )}
      {impersonateMode && isActualAdmin && (
        <ImpersonateViewingBar
          email={localStorage.getItem("impersonateEmail")}
          name={userInfo?.name}
          onOpenDrawer={() => setViewAsOpen(true)}
          onRevert={handleRevert}
        />
      )}
      {((!impersonateMode && !!userInfo?.role?.includes("Admin")) || (impersonateMode && isActualAdmin)) && (
        <ImpersonateDrawer
          isOpen={viewAsOpen}
          onClose={() => setViewAsOpen(false)}
          viewingEmail={impersonateMode && isActualAdmin ? localStorage.getItem("impersonateEmail") : null}
        />
      )}

      {/* 휴대폰 언어 바텀시트 — ≡ 메뉴 첫 줄에서 연다 */}
      <LanguageSheet isOpen={langSheetOpen} onClose={() => setLangSheetOpen(false)} />
    </Box>
  );
}
